import React, { FC, useState, useEffect, useCallback } from 'react'
import DocumentTitle from 'react-document-title'
import { useHistory, useParams } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import { Toast, useToastState, ToastVariation } from 'transfix-ui/components/Toast'
import { Box, makeStyles } from 'transfix-ui/core'
import {
  ITransactionSetTypes,
  ITransactionSetSubtypes,
  ITransactionSetWithSubtype,
  IConfigurableBuilderTest,
  IConfigurableBuilder,
  ICreateConfigurableBuilderInput,
  IUpdateConfigurableBuilderInput,
  IStupidEdiConfigs,
  IBuilderLibraries,
  Maybe,
} from 'types/graphqlTypes'
import { useModal } from 'utils/hooks'
import { getParserConfigs } from '../EdiSelfService/api'
import { tfx12Config } from '../EdiSelfService/types'
import { EDI_TOOLS_ROUTES } from '../LandingPage'
import { BuilderWarningsModal } from './components/BuilderWarningsModal'
import { ConfigurableBuilderOptionsModal } from './components/ConfigurableBuilderOptionsModal'
import { EDIOutboundHeader } from './components/EDIOutboundHeader'
import { LoadBuilderModal } from './components/LoadBuilderModal'
import { OutboundInputOptionsModal } from './components/OutboundInputOptionsModal'
import { QueryContainer } from './components/QueryContainer'
import { QueryResults } from './components/QueryResults'
import { SampleDataContainer } from './components/SampleDataContainer'
import { TestManagement } from './components/TestManagement'
import { useConfigurableBuilder } from './hooks/useConfigurableBuilder'
import { useConfigurableBuilderTest } from './hooks/useConfigurableBuilderTest'
import { useGetConfigurableBuilderWarnings } from './hooks/useGetConfigurableBuilderWarnings'
import { useSerializedJsonLoader } from './hooks/useSerializedJsonLoader'
import { DEFAULT_TRANSACTION_SET } from './hooks/useTransactionSets'
import { IConfigurableBuilderOptionsSchema } from './schema/ConfigurableBuilderOptionsSchema'

interface IEdiSelfServiceOutboundProps {}

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(8),
    '& .dpl-toast': {
      whiteSpace: 'pre-wrap',
    },
  },
  row: {
    width: '100%',
    direction: 'ltr',
    display: 'flex',
    alignItems: 'left',
    justifyContent: 'left',
  },
}))

// A set of default outbound input options which can be used after a
// new builder is loaded. Values chosen mostly randomly, since user
// will probably load a builder test and override these defaults.
// These are here mainly to allow quick ad hoc testing with a new builder.
const DEFAULT_BUILDER_TEST_DATA = {
  id: '',
  name: '',
  notes: '',
  isLive: true,
  changedField: 'arrived_at',
  changedShipperStopSequence: 1,
  shipmentId: 1108588,
  accountingInvoiceId: 400530,
  expectedOutput: [],
}

// placeholder with the proper format, to allow a user to start filling in a real template immediately
const DEFAULT_EXTRACTION_TEMPLATE = '{"segments":[[]]}'

export const EdiSelfServiceOutbound: FC<IEdiSelfServiceOutboundProps> = () => {
  const history = useHistory()
  const { builderId } = useParams<{ builderId: string }>()
  const { builderVersionId } = useParams<{ builderVersionId: string }>()
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const { closeModal, isOpen, openModal } = useModal()

  const validTestDataForMessageType = (
    allValues: IConfigurableBuilderTest,
    transactionSetWithSubtype: ITransactionSetWithSubtype
  ): IConfigurableBuilderTest => {
    const finalValue: IConfigurableBuilderTest = {
      id: allValues.id,
      name: allValues.name,
      notes: allValues.notes,
      isLive: allValues.isLive,
      expectedOutput: allValues.expectedOutput,
    }

    // an unsaved test should not have expectedOutput
    if (!finalValue.id) {
      finalValue.expectedOutput = []
    }

    switch (transactionSetWithSubtype.transactionSet) {
      case ITransactionSetTypes._990: {
        finalValue.shipmentId = allValues.shipmentId
        break
      }
      case ITransactionSetTypes._214: {
        finalValue.shipmentId = allValues.shipmentId
        if (
          transactionSetWithSubtype.transactionSetSubtype !==
          ITransactionSetSubtypes.SCHEDULED_TRACKING
        ) {
          finalValue.changedField = allValues.changedField
          finalValue.changedShipperStopSequence = allValues.changedShipperStopSequence
        }
        break
      }
      case ITransactionSetTypes._210: {
        finalValue.accountingInvoiceId = allValues.accountingInvoiceId
        break
      }
      default: {
        break
      }
    }
    return finalValue
  }

  const displayUserFeedback = (message: string, level?: 'error' | 'info'): void => {
    let toastType: ToastVariation = 'default'
    if (level === 'error') {
      toastType = 'alert'
    }

    openToast({
      toastType,
      toastMessage: message,
    })
  }

  const [extractionTemplate, setExtractionTemplate] = useState<string>(DEFAULT_EXTRACTION_TEMPLATE)
  const [builderLibrary, setBuilderLibrary] = useState<IBuilderLibraries>(
    IBuilderLibraries.STUPIDEDI
  )
  const [stupidediConfigOptions, setStupidediConfigOptions] = useState<string[]>([])
  const [tfx12ConfigOptions, setTfx12ConfigOptions] = useState<tfx12Config[]>([])
  const [stupidediConfig, setStupidediConfig] = useState<Maybe<IStupidEdiConfigs>>(
    IStupidEdiConfigs.DEFAULT
  )
  const [tfx12DefinitionId, setTfx12DefinitionId] = useState<Maybe<string>>('')

  const [canBuildBalanceDues, setCanBuildBalanceDues] = useState<boolean>(false)

  const initializeConfigOptions = async () => {
    const configs = await getParserConfigs()
    setStupidediConfigOptions(configs.stupidediConfigs)
    setTfx12ConfigOptions(configs.tfx12Configs)
  }

  const displayError = (error: string) => {
    openToast({
      toastType: 'alert',
      toastMessage: `${error}`,
    })
  }

  useEffectOnce(() => {
    try {
      initializeConfigOptions()
    } catch (e) {
      displayError(e)
    }
  })

  const [showResults, setShowResults] = useState<boolean>(false)
  const [transactionSetWithSubtype, setTransactionSetWithSubtype] = useState<
    ITransactionSetWithSubtype
  >(DEFAULT_TRANSACTION_SET)

  const [currentBuilderTest, setCurrentBuilderTest] = useState<IConfigurableBuilderTest>(
    validTestDataForMessageType(DEFAULT_BUILDER_TEST_DATA, transactionSetWithSubtype)
  )

  const { isLoadingSerializedJson, serializedJsonData } = useSerializedJsonLoader({
    transactionSetWithSubtype,
    displayUserFeedback,
    options: currentBuilderTest,
  })

  const {
    configurableBuilderOptions,
    configurableBuilders,
    createConfigurableBuilder,
    ediResult,
    isLoading: isLoadingBuilder,
    jsonResult,
    runQuery,
    selectedConfigurableBuilder,
    setConfigurableBuilderOptions,
    setEdiResult,
    setJsonResult,
    setSelectedConfigurableBuilder,
    updateConfigurableBuilder,
  } = useConfigurableBuilder(displayUserFeedback)

  const {
    configurableBuilderTests,
    createConfigurableBuilderTest,
    invalidateLatestTestResults,
    latestTestResults,
    runConfigurableBuilderTests,
    testsAreRunning,
    updateConfigurableBuilderTest,
  } = useConfigurableBuilderTest({
    builderId: selectedConfigurableBuilder?.id,
    displayUserFeedback,
    setCurrentBuilderTest,
  })

  useEffect(() => {
    setCurrentBuilderTest(
      validTestDataForMessageType(DEFAULT_BUILDER_TEST_DATA, transactionSetWithSubtype)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionSetWithSubtype])

  // if current input options or extraction template are updated, then the
  // results are no longer valid so we'll hide them
  useEffect(() => {
    setShowResults(false)
  }, [extractionTemplate, currentBuilderTest, stupidediConfig])

  // if extractionTemplate is altered then latest test results are no longer valid
  useEffect(() => {
    invalidateLatestTestResults()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractionTemplate, stupidediConfig])

  // when builder is unloaded, clear everything
  useEffect(() => {
    setCurrentBuilderTest(
      validTestDataForMessageType(DEFAULT_BUILDER_TEST_DATA, transactionSetWithSubtype)
    )
    invalidateLatestTestResults()
    setExtractionTemplate('')
    setBuilderLibrary(IBuilderLibraries.STUPIDEDI)
    setStupidediConfig(IStupidEdiConfigs.DEFAULT)
    setTfx12DefinitionId('')
    setJsonResult('')
    setEdiResult('')
    setCanBuildBalanceDues(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfigurableBuilder])

  // change which test is 'current'
  // this may be a saved test loaded from the server, or it may be ad-hoc test data
  // entered by the user. (saved tests have an id, adhoc do not.)
  const updateCurrentBuilderTestData = (newValues: IConfigurableBuilderTest) => {
    // merge newValues into currentBuilderTest
    const merged = {
      ...currentBuilderTest,
      ...newValues,
    }
    const updatedBuilderTest = validTestDataForMessageType(merged, transactionSetWithSubtype)

    if (!isEqual(updatedBuilderTest, currentBuilderTest)) {
      setCurrentBuilderTest(updatedBuilderTest)

      // if we've changed test input data, need to invalidate any result we have locally for that test.
      if (updatedBuilderTest.id && currentBuilderTest.id === updatedBuilderTest.id) {
        invalidateLatestTestResults(updatedBuilderTest.id)
      }
    }
  }

  const classes = useStyles()

  const runQueryHandler = (
    extractionTemplate: string,
    builderLibrary: IBuilderLibraries,
    stupidediConfig: Maybe<IStupidEdiConfigs>,
    tfx12DefinitionId: Maybe<string>
  ) => {
    if (!showResults) {
      setShowResults(true)
    }
    runQuery({
      outboundInputOptions: currentBuilderTest,
      extractionTemplate: extractionTemplate || '',
      builderLibrary,
      stupidediConfig,
      tfx12DefinitionId,
      transactionSet: transactionSetWithSubtype.transactionSet,
      transactionSetSubtype: transactionSetWithSubtype.transactionSetSubtype,
    })
  }

  const handleSetConfigurableBuilderOptions = (options: IConfigurableBuilderOptionsSchema) => {
    setConfigurableBuilderOptions(options)
  }

  const handleCreateConfigurableBuilder = (data: ICreateConfigurableBuilderInput) => {
    createConfigurableBuilder(data.name, data.transactionSet, data?.transactionSetSubtype)
  }

  const handleUpdateConfigurableBuilder = (data: IUpdateConfigurableBuilderInput) => {
    updateConfigurableBuilder(
      data.id,
      data?.name,
      data?.transactionSet,
      data?.transactionSetSubtype,
      data?.isLive
    )
  }

  const handleSetSelectedConfigurableBuilder = useCallback(
    (data: IConfigurableBuilder | null) => {
      if (data) {
        const newTypeWithSubtype = pick(data, ['transactionSet', 'transactionSetSubtype'])
        setSelectedConfigurableBuilder(data)
        setTransactionSetWithSubtype(newTypeWithSubtype)
        setCurrentBuilderTest(
          validTestDataForMessageType(DEFAULT_BUILDER_TEST_DATA, newTypeWithSubtype)
        )
        setJsonResult('')
        setEdiResult('')
      } else {
        setSelectedConfigurableBuilder(null)
      }
    },
    [setEdiResult, setJsonResult, setSelectedConfigurableBuilder]
  )

  useEffect(() => {
    if (builderId) {
      const configurableBuilder = configurableBuilders?.find(builder => builder.id === builderId)
      if (configurableBuilder) {
        handleSetSelectedConfigurableBuilder(configurableBuilder)
      }
    }
  }, [builderId, configurableBuilders, handleSetSelectedConfigurableBuilder])

  const { configurableBuilderWarnings } = useGetConfigurableBuilderWarnings({
    displayUserFeedback,
    builderId: selectedConfigurableBuilder?.id,
    outboundConfigId: configurableBuilderOptions?.outboundEdiMapperConnectionConfigId,
  })

  const builderChangeHandler = (builder: IConfigurableBuilder | null) => {
    if (builder) {
      history.push(`${EDI_TOOLS_ROUTES.SELF_SERVE_OUTBOUND}/builder/${builder.id}`)
    }
  }

  const clearSelectedBuilder = () => {
    setSelectedConfigurableBuilder(null)
    history.push(EDI_TOOLS_ROUTES.SELF_SERVE_OUTBOUND)
  }

  return (
    <DocumentTitle title='EDI Self Service Outbound' data-test='edi-self-service-outbound'>
      <Box component='div' className={classes.container}>
        <EDIOutboundHeader
          onClearBuilderClick={clearSelectedBuilder}
          onLoadBuilderClick={openModal('loadBuilderModal')}
          onBuilderWarningsClick={openModal('builderWarningsModal')}
          selectedBuilder={selectedConfigurableBuilder}
        />
        <Toast
          duration={null}
          variation={toastType}
          open={toastOpen}
          onClose={closeToast}
          message={toastMessage}
        />
        {isOpen('outboundInputOptionsModal') && (
          <OutboundInputOptionsModal
            transactionSetWithSubtype={transactionSetWithSubtype}
            onCancel={closeModal}
            onClose={closeModal}
            onSubmit={updateCurrentBuilderTestData}
            options={currentBuilderTest}
          />
        )}
        {isOpen('configurableBuilderOptionsModal') && (
          <ConfigurableBuilderOptionsModal
            transactionSetWithSubtype={transactionSetWithSubtype}
            onCancel={closeModal}
            onClose={closeModal}
            onSubmit={handleSetConfigurableBuilderOptions}
            options={configurableBuilderOptions}
          />
        )}
        {isOpen('loadBuilderModal') && (
          <LoadBuilderModal
            builders={configurableBuilders || []}
            onCancel={closeModal}
            onClose={closeModal}
            onCreateBuilder={handleCreateConfigurableBuilder}
            onEditBuilder={handleUpdateConfigurableBuilder}
            onSelectBuilder={builderChangeHandler}
          />
        )}
        {isOpen('builderWarningsModal') && (
          <BuilderWarningsModal
            builderName={selectedConfigurableBuilder?.name}
            builderWarnings={configurableBuilderWarnings || []}
            onCancel={closeModal}
            onClose={closeModal}
          />
        )}
        <Box component='div' className={classes.row} mb={4}>
          <SampleDataContainer
            isLoading={isLoadingSerializedJson}
            transactionSetWithSubtype={transactionSetWithSubtype}
            onSettingsClick={openModal('outboundInputOptionsModal')}
            serializedJsonData={serializedJsonData || ''}
          />
          <QueryContainer
            displayUserFeedback={displayUserFeedback}
            runBuilderSettingsClickHandler={openModal('configurableBuilderOptionsModal')}
            runQueryHandler={runQueryHandler}
            extractionTemplate={extractionTemplate}
            setExtractionTemplate={setExtractionTemplate}
            builderLibrary={builderLibrary}
            setBuilderLibrary={setBuilderLibrary}
            stupidediConfig={stupidediConfig}
            setStupidediConfig={setStupidediConfig}
            stupidediConfigOptions={stupidediConfigOptions}
            tfx12DefinitionId={tfx12DefinitionId}
            setTfx12DefinitionId={setTfx12DefinitionId}
            tfx12ConfigOptions={tfx12ConfigOptions}
            selectedBuilder={selectedConfigurableBuilder}
            outboundConfigId={configurableBuilderOptions.outboundEdiMapperConnectionConfigId}
            builderVersionId={builderVersionId}
            canBuildBalanceDues={canBuildBalanceDues}
            setCanBuildBalanceDues={setCanBuildBalanceDues}
            transactionSetWithSubtype={transactionSetWithSubtype}
          />
        </Box>
        {showResults && (
          <QueryResults
            isLoading={isLoadingBuilder}
            jsonResult={jsonResult}
            ediResult={ediResult}
          />
        )}
        <TestManagement
          builderId={selectedConfigurableBuilder?.id}
          extractionTemplate={extractionTemplate}
          builderLibrary={builderLibrary}
          stupidediConfig={stupidediConfig}
          tfx12DefinitionId={tfx12DefinitionId}
          outboundConfigId={configurableBuilderOptions?.outboundEdiMapperConnectionConfigId}
          jsonResult={jsonResult}
          ediResult={ediResult}
          currentBuilderTest={currentBuilderTest}
          setCurrentBuilderTest={updateCurrentBuilderTestData}
          configurableBuilderTests={configurableBuilderTests}
          createConfigurableBuilderTest={createConfigurableBuilderTest}
          latestTestResults={latestTestResults}
          testsAreRunning={testsAreRunning}
          runConfigurableBuilderTests={runConfigurableBuilderTests}
          updateConfigurableBuilderTest={updateConfigurableBuilderTest}
        />
      </Box>
    </DocumentTitle>
  )
}
